import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

// Define the shape of profile data
export interface ProfileData {
  id?: string;
  user_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  created_at?: string;
  role?: 'owner' | 'admin' | 'reviewer';
}

// Define the context interface
interface ProfileContextType {
  profileData: ProfileData | null;
  isLoading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  updateProfile: (data: Partial<ProfileData>) => Promise<boolean>;
}

// Create the context with default values
const ProfileContext = createContext<ProfileContextType>({
  profileData: null,
  isLoading: false,
  error: null,
  refreshProfile: async () => {},
  updateProfile: async () => false,
});

// Hook to use the profile context
export const useProfile = () => useContext(ProfileContext);

// Provider component
interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch profile data
  const fetchProfileData = async (userId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('Profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
        
      if (error) {
        throw error;
      }
      
      setProfileData(data);
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError(err.message || 'Failed to fetch profile data');
      setProfileData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to refresh profile data
  const refreshProfile = async () => {
    if (user) {
      await fetchProfileData(user.id);
    }
  };

  // Function to update profile data
  const updateProfile = async (data: Partial<ProfileData>): Promise<boolean> => {
    if (!user) {
      console.error('[updateProfile] No user found');
      return false;
    }
    
    console.log('[updateProfile] Starting profile update:', { userId: user.id, updateData: data });
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if profile exists
      const { data: existingProfile, error: checkError } = await supabase
        .from('Profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "no rows returned" error
        console.error('[updateProfile] Error checking existing profile:', checkError);
        throw checkError;
      }
      
      console.log('[updateProfile] Existing profile check:', { exists: !!existingProfile });
      
      let result;
      
      if (existingProfile) {
        // Update existing profile
        console.log('[updateProfile] Updating existing profile:', { profileId: existingProfile.id });
        result = await supabase
          .from('Profiles')
          .update(data)
          .eq('user_id', user.id)
          .select();
      } else {
        // Create new profile
        console.log('[updateProfile] Creating new profile');
        result = await supabase
          .from('Profiles')
          .insert({
            user_id: user.id,
            email: user.email,
            ...data
          })
          .select();
      }
      
      if (result.error) {
        console.error('[updateProfile] Error updating profile:', result.error);
        throw result.error;
      }
      
      console.log('[updateProfile] Profile update successful:', result.data);
      
      // Refresh profile data after update
      await refreshProfile();
      console.log('[updateProfile] Profile refreshed');
      return true;
    } catch (err: any) {
      console.error('[updateProfile] Error during profile update:', err);
      setError(err.message || 'Failed to update profile');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch profile data when user changes
  useEffect(() => {
    if (user) {
      fetchProfileData(user.id);
    } else {
      setProfileData(null);
    }
  }, [user]);

  // Context value
  const value = {
    profileData,
    isLoading,
    error,
    refreshProfile,
    updateProfile,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileContext; 